/**
 * ═══ LA RÉVÉLATION DES RÉSULTATS ═══
 *
 * Ce qui se passe à l'écran au moment où la régie appuie sur « Montrer les
 * résultats ». Réglé sur l'activité, à sa préparation (`ActivityEditor`), et rangé
 * dans son `config` sous une seule clé, `reveal` : une animation et ses options.
 * Absente, les résultats s'affichent comme avant, en barres.
 *
 * ⚠️ UNE ANIMATION CHANGE AUSSI L'AFFICHAGE. Le podium n'est pas un effet posé sur
 * les barres : les résultats deviennent des marches, et c'est la hauteur qui dit le
 * rang. C'est pour ça qu'il vit ici, en règles, et que `Results.tsx` ne fait que les
 * dessiner.
 *
 * ⚠️ `reveal` EST DANS LA LISTE BLANCHE DE L'API (`PUBLIC_CONFIG_KEYS`,
 * `api/src/helpers/live.js`). Sans elle, seule la régie le recevrait : l'écran de salle
 * et les téléphones afficheraient des barres, et l'organisateur verrait le podium dans
 * son aperçu sans que la salle le voie jamais.
 */

import type { ActivityType } from "./types";

/** Les animations connues. Les suivantes s'ajoutent ici, et dans `REVEALS_BY_TYPE`. */
export type RevealAnimation = "podium" | "answer" | "verdict";

/**
 * QUELLE ANIMATION VA AVEC QUEL TYPE.
 *
 * ⚠️ « LA BONNE RÉPONSE » EST AU QUIZ SEUL : c'est le seul type qui en a une. Et « LE
 * VERDICT » n'est pas au quiz : une question à bonne réponse ne s'adopte pas. Une
 * animation qui ne va pas avec le type — réglée à la main en base, ou restée d'un type
 * changé à la création — retombe sur les barres plutôt que de dessiner n'importe quoi.
 */
export const REVEALS_BY_TYPE: Partial<Record<ActivityType, readonly RevealAnimation[]>> = {
  poll: ["podium", "verdict"],
  quiz: ["podium", "answer"],
  ballot: ["podium", "verdict"],
};

/** Le seuil d'un verdict : plus de la moitié, ou au moins les deux tiers. */
export type Majority = "half" | "two-thirds";

/** Ce qui est rangé dans `config.reveal`. Tout est facultatif sauf l'animation. */
export type Reveal = {
  animation: RevealAnimation;
  /** La phrase d'annonce au-dessus. Vide : celle du dictionnaire. */
  kicker?: string | null;
  /** Une pluie de confettis sur le gagnant. Coupée pour une assemblée générale. */
  confetti?: boolean;
  /** Podium : combien de places, les trois premiers ou le gagnant seul. */
  places?: number;
  /** Podium : les réponses qui ne montent pas, listées en dessous. */
  rest?: boolean;
  /** Podium : des marches or, argent, bronze ; sinon aux couleurs de l'événement. */
  medals?: boolean;
  /** Verdict : le seuil. */
  majority?: Majority;
  /** Verdict : la réponse qui vaut « pour », par sa place dans l'activité. */
  approve?: number;
  /** Verdict : la réponse qui vaut abstention, hors des suffrages exprimés. */
  abstain?: number | null;
  /** Verdict : le mot du tampon, adoptée ou rejetée. Vide : ceux du dictionnaire. */
  adopted?: string | null;
  rejected?: string | null;
};

/** Toutes les options, défauts compris : ce que le formulaire tient en main. */
export type RevealOptions = {
  kicker: string;
  confetti: boolean;
  places: 1 | 3;
  rest: boolean;
  medals: boolean;
  majority: Majority;
  approve: number;
  abstain: number | null;
  adopted: string;
  rejected: string;
};

/** Chaque animation telle qu'on l'applique : ses options à elle, rien de facultatif. */
export type PodiumReveal = { animation: "podium" } & Pick<
  RevealOptions,
  "kicker" | "confetti" | "places" | "rest" | "medals"
>;
export type AnswerReveal = { animation: "answer" } & Pick<RevealOptions, "kicker" | "confetti">;
export type VerdictReveal = { animation: "verdict" } & Pick<
  RevealOptions,
  "kicker" | "confetti" | "majority" | "approve" | "abstain" | "adopted" | "rejected"
>;
export type ActiveReveal = PodiumReveal | AnswerReveal | VerdictReveal;

export const REVEAL_DEFAULTS: RevealOptions = {
  kicker: "",
  confetti: true,
  places: 3,
  rest: true,
  /* ⚠️ LES MÉDAILLES PAR DÉFAUT, PAS LA MARQUE. Aux couleurs de l'événement, la
     première marche prend `--gk-brand-500` et les autres ses teintes claires : sur une
     palette réglée à la main, la teinte claire peut être la plus vive, et la marche
     du gagnant ressortait MOINS que les deux autres. Vu sur l'événement 706. L'or ne
     dépend d'aucune palette. */
  medals: true,
  majority: "half",
  approve: 0,
  abstain: null,
  adopted: "",
  rejected: "",
};

const ANIMATIONS: readonly RevealAnimation[] = ["podium", "answer", "verdict"];
const text = (value: unknown) => (typeof value === "string" ? value.trim() : "");
const flag = (value: unknown, fallback: boolean) =>
  typeof value === "boolean" ? value : fallback;
const place = (value: unknown) =>
  Number.isInteger(value) && (value as number) >= 0 ? (value as number) : null;

/** Les options d'un `config.reveal` brut, défauts compris. Rien n'y est cru sur parole. */
function optionsOf(raw: Partial<Reveal>): RevealOptions {
  const approve = place(raw.approve) ?? REVEAL_DEFAULTS.approve;
  const abstain = place(raw.abstain);
  return {
    kicker: text(raw.kicker),
    confetti: flag(raw.confetti, REVEAL_DEFAULTS.confetti),
    places: Number(raw.places) === 1 ? 1 : 3,
    rest: flag(raw.rest, REVEAL_DEFAULTS.rest),
    medals: flag(raw.medals, REVEAL_DEFAULTS.medals),
    majority: raw.majority === "two-thirds" ? "two-thirds" : "half",
    approve,
    /* Une réponse ne peut pas valoir à la fois « pour » et abstention. */
    abstain: abstain === approve ? null : abstain,
    adopted: text(raw.adopted),
    rejected: text(raw.rejected),
  };
}

/** Une animation et ses seules options. */
export function revealOf(animation: RevealAnimation, options: RevealOptions): ActiveReveal {
  const { kicker, confetti } = options;
  if (animation === "answer") return { animation, kicker, confetti };
  if (animation === "verdict") {
    const { majority, approve, abstain, adopted, rejected } = options;
    return { animation, kicker, confetti, majority, approve, abstain, adopted, rejected };
  }
  const { places, rest, medals } = options;
  return { animation, kicker, confetti, places, rest, medals };
}

/**
 * LE RÉGLAGE D'UNE ACTIVITÉ, ou `null` sans animation.
 *
 * ⚠️ LU SANS CONFIANCE. `config` est un blob JSON libre côté serveur : une valeur
 * écrite à la main en base, ou par une version future, ne doit ni planter l'écran de
 * salle ni afficher un podium à zéro place. Ce qui ne se comprend pas retombe sur le
 * défaut, et une animation inconnue — ou qui ne va pas avec le type — sur les barres.
 */
export function readReveal(
  config: { reveal?: unknown } | null | undefined,
  type?: ActivityType,
): ActiveReveal | null {
  const raw = config?.reveal;
  if (!raw || typeof raw !== "object") return null;
  const reveal = raw as Partial<Reveal>;
  const animation = reveal.animation;
  if (!animation || !ANIMATIONS.includes(animation)) return null;
  if (type && !(REVEALS_BY_TYPE[type] ?? []).includes(animation)) return null;
  return revealOf(animation, optionsOf(reveal));
}

/**
 * CE QUE LE FORMULAIRE REPREND : l'animation choisie, et TOUTES les options lues.
 *
 * Toutes, et pas seulement celles de l'animation : passer du podium au verdict puis
 * revenir retrouve les places qu'on avait réglées, le temps d'hésiter.
 */
export function draftOf(
  config: { reveal?: unknown } | null | undefined,
  type: ActivityType,
): { animation: RevealAnimation | null; options: RevealOptions } {
  const raw = config?.reveal;
  const options =
    raw && typeof raw === "object" ? optionsOf(raw as Partial<Reveal>) : { ...REVEAL_DEFAULTS };
  return { animation: readReveal(config, type)?.animation ?? null, options };
}

/**
 * LE `config` À ENVOYER, avec ou sans la révélation.
 *
 * ⚠️ LA MODIFICATION REMPLACE TOUT LE `config` (`routes/live/activities/update.js`),
 * elle ne fusionne pas. D'où la base : ce qu'on ne règle pas dans le formulaire
 * (`display`, `revealRightAnswer`…) doit repartir tel quel, sinon enregistrer une
 * faute de frappe corrigée effacerait le reste. Et « sans animation » RETIRE la clé :
 * la laisser à `null` la ferait voyager pour rien.
 *
 * ⚠️ LES MOTS VIDES NE S'ÉCRIVENT PAS : c'est alors celui du dictionnaire qui
 * s'affiche, et il se traduit, lui.
 */
export function withReveal<C extends object>(
  base: C,
  reveal: ActiveReveal | null,
): Omit<C, "reveal"> & { reveal?: Reveal } {
  const { reveal: _dropped, ...rest } = base as C & { reveal?: unknown };
  if (!reveal) return rest;
  const written: Reveal = { animation: reveal.animation, confetti: reveal.confetti };
  if (reveal.kicker.trim()) written.kicker = reveal.kicker.trim();
  if (reveal.animation === "podium") {
    written.places = reveal.places;
    written.rest = reveal.rest;
    written.medals = reveal.medals;
  }
  if (reveal.animation === "verdict") {
    written.majority = reveal.majority;
    written.approve = reveal.approve;
    if (reveal.abstain !== null && reveal.abstain !== reveal.approve) {
      written.abstain = reveal.abstain;
    }
    if (reveal.adopted.trim()) written.adopted = reveal.adopted.trim();
    if (reveal.rejected.trim()) written.rejected = reveal.rejected.trim();
  }
  return { ...rest, reveal: written };
}

/**
 * ═══ LE VERDICT ═══
 *
 * La part des suffrages EXPRIMÉS qui vaut « pour », et ce qu'elle décide. L'abstention,
 * quand l'organisateur en a désigné une, n'est pas un suffrage exprimé : elle sort du
 * total, comme dans un procès-verbal d'assemblée.
 *
 * ⚠️ SUR LES VOIX QUAND ON LES A, sur les parts sinon. Les voix sont des entiers (déjà
 * pondérées par le serveur), et deux tiers de 3 font exactement 2 ; les parts, elles,
 * sont des divisions, et 2/3 recalculé peut tomber une virgule sous 2/3. D'où la
 * tolérance, qui ne sert qu'aux parts : sans elle, une résolution votée pile aux deux
 * tiers serait déclarée rejetée quand l'organisateur masque le nombre de votants.
 *
 * ⚠️ « PLUS DE LA MOITIÉ », STRICTEMENT : à égalité, la résolution n'est pas adoptée.
 * « Au moins les deux tiers », en revanche, adopte à deux tiers pile.
 */
const EPSILON = 1e-9;

export type Verdict = {
  /** La part des suffrages exprimés qui vaut « pour », entre 0 et 1. */
  ratio: number;
  /** Le seuil, entre 0 et 1. */
  threshold: number;
  adopted: boolean;
};

export function verdictOf(
  rows: ReadonlyArray<{ index: number; share: number; votes?: number }>,
  { approve, abstain, majority }: Pick<VerdictReveal, "approve" | "abstain" | "majority">,
): Verdict | null {
  const pro = rows.find((row) => row.index === approve);
  if (!pro) return null;
  const off = abstain === null || abstain === approve ? undefined : rows.find((row) => row.index === abstain);

  const counted = rows.every((row) => typeof row.votes === "number");
  let ratio: number;
  if (counted) {
    const total = rows.reduce((sum, row) => sum + (row.votes ?? 0), 0);
    const expressed = total - (off?.votes ?? 0);
    if (expressed <= 0) return null;
    ratio = (pro.votes ?? 0) / expressed;
  } else {
    const expressed = 1 - (off?.share ?? 0);
    const any = rows.some((row) => row.share > 0);
    if (!any || expressed <= EPSILON) return null;
    ratio = pro.share / expressed;
  }

  const threshold = majority === "two-thirds" ? 2 / 3 : 1 / 2;
  const adopted =
    majority === "two-thirds" ? ratio >= threshold - EPSILON : ratio > threshold + EPSILON;
  return { ratio: Math.min(1, Math.max(0, ratio)), threshold, adopted };
}

/**
 * QUI VAUT « POUR », QUI VAUT ABSTENTION, À DÉFAUT D'AVOIR ÉTÉ DIT.
 *
 * Le formulaire propose ce que les libellés disent déjà : « Pour » ou « Oui » d'abord,
 * sinon la première réponse ; « Abstention » s'il y en a une. L'organisateur corrige
 * s'il le faut — ce n'est qu'une proposition, écrite en clair sous ses yeux.
 */
export function guessVerdict(labels: readonly string[]): { approve: number; abstain: number | null } {
  const normal = labels.map((label) =>
    label
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase(),
  );
  const approve = normal.findIndex((label) => /^(pour|oui|yes|for|favorable)\b/.test(label));
  const abstain = normal.findIndex((label) => /^(abst|blanc|ne se prononce)/.test(label));
  return {
    approve: approve >= 0 ? approve : 0,
    abstain: abstain >= 0 && abstain !== (approve >= 0 ? approve : 0) ? abstain : null,
  };
}

/**
 * ═══ LA BONNE RÉPONSE : QUI S'ÉTEINT, ET QUAND ═══
 *
 * Les mauvaises réponses s'éteignent une à une, de la MOINS choisie à la plus choisie :
 * la fausse piste que la salle a suivie tient le plus longtemps, c'est elle qui fait
 * le suspense. Puis la bonne s'allume. Plusieurs bonnes réponses s'allument ensemble.
 *
 * ⚠️ LA DURÉE EST BORNÉE, PAS L'ÉCART. Sept dixièmes entre deux réponses, c'est juste
 * pour quatre ; pour douze, la salle attendrait huit secondes devant des tuiles qui
 * s'éteignent. Au-delà, l'écart se resserre pour tenir dans `ANSWER_TIMING.span`.
 *
 * `null` quand aucune réponse n'est marquée bonne : il n'y a rien à révéler, et
 * l'écran retombe sur les barres.
 */
export const ANSWER_TIMING = {
  /** Les tuiles apparaissent, l'une après l'autre. */
  stagger: 0.08,
  /** La première mauvaise réponse s'éteint. */
  first: 1.2,
  /** L'écart entre deux, au plus. */
  between: 0.7,
  /** Le temps que prennent toutes les mauvaises, au plus. */
  span: 3.5,
  /** Le temps de plus avant la bonne. */
  suspense: 0.8,
  /** Après la bonne, les parts. */
  after: 0.6,
} as const;

export function answerTimeline(
  rows: ReadonlyArray<{ index: number; share: number; right: boolean }>,
): { at: Map<number, number>; done: number } | null {
  if (!rows.some((row) => row.right)) return null;
  const wrong = rows
    .filter((row) => !row.right)
    .slice()
    .sort((a, b) => a.share - b.share || b.index - a.index);
  const gap = wrong.length > 1
    ? Math.min(ANSWER_TIMING.between, ANSWER_TIMING.span / (wrong.length - 1))
    : 0;
  const at = new Map<number, number>();
  wrong.forEach((row, i) => at.set(row.index, round(ANSWER_TIMING.first + i * gap)));
  const last = wrong.length ? ANSWER_TIMING.first + (wrong.length - 1) * gap : ANSWER_TIMING.first;
  const done = round(last + ANSWER_TIMING.suspense);
  rows.filter((row) => row.right).forEach((row) => at.set(row.index, done));
  return { at, done };
}

/**
 * COMBIEN DE COLONNES POUR LES TUILES, sur le mur.
 *
 * ⚠️ PAS « AUTANT QUE LA LARGEUR EN PERMET » : quatre réponses donnaient trois tuiles
 * puis une orpheline, seule sur sa ligne, qu'on lisait comme la réponse mise à part.
 * Des lignes pleines : 2 × 2 pour quatre, trois colonnes jusqu'à six (et pour neuf),
 * quatre au-delà. Le téléphone en prend deux au plus (voir `live.css`).
 */
export function tileColumns(count: number): number {
  if (count <= 3) return Math.max(1, count);
  if (count === 4) return 2;
  if (count <= 6 || count === 9) return 3;
  return 4;
}

/**
 * LE MINUTAGE DU VERDICT : la jauge se remplit, puis le tampon tombe. La jauge prend
 * son temps — c'est pendant qu'elle monte vers le seuil que la salle retient son souffle.
 */
export const VERDICT_TIMING = {
  /** La jauge commence à se remplir. */
  start: 0.4,
  /** Le temps qu'elle met. */
  fill: 2.2,
  /** Le tampon, un temps après. */
  stamp: 0.3,
  /** Après le tampon, le détail. */
  after: 0.8,
} as const;

/** Une ligne de résultat et son rang. */
export type Placed<R> = { row: R; rank: number };

/**
 * LES RANGS, EX ÆQUO COMPRIS : 1, 1, 3.
 *
 * ⚠️ DEUX PARTS ÉGALES ONT LE MÊME RANG. Le tableau les range l'une sous l'autre dans
 * l'ordre de l'organisateur (`tallyRows`), ce qui suffit pour des barres qu'on compare
 * à l'œil ; sur un podium, mettre l'une plus haut que l'autre proclamerait un gagnant
 * que les votes n'ont pas désigné. Les lignes arrivent triées, la plus choisie en tête.
 */
export function ranked<R extends { share: number }>(rows: readonly R[]): Placed<R>[] {
  /* La première ligne à cette part donne son rang à toutes les autres. */
  return rows.map((row) => ({
    row,
    rank: rows.findIndex((other) => other.share === row.share) + 1,
  }));
}

/**
 * LE PODIUM ET CE QUI RESTE EN DESSOUS.
 *
 * ⚠️ MONTE SUR LE PODIUM QUI A LE RANG, PAS LES N PREMIÈRES LIGNES. Avec deux réponses
 * à égalité pour la troisième place, en prendre trois en laisserait une sur la marche
 * et l'autre dans la liste, au même score : un classement qui tranche là où les votes
 * ne l'ont pas fait. Le podium s'élargit plutôt. Même règle pour « le gagnant seul » :
 * deux premières ex æquo montent toutes les deux.
 *
 * ⚠️ ZÉRO VOIX NE MONTE PAS. Une réponse que personne n'a choisie n'a rien gagné, et
 * au début d'un vote montré en direct, le podium entier serait fait de 0 %.
 *
 * ⚠️ LE PREMIER AU MILIEU, puis on alterne gauche, droite — la même règle que le
 * classement (`podiumOrder`), prolongée au-delà de trois pour les égalités. C'est le
 * seul endroit où l'ordre d'affichage n'est pas l'ordre du classement.
 */
export function podiumOf<R extends { share: number }>(
  rows: readonly R[],
  places: number,
): { steps: Placed<R>[]; rest: Placed<R>[] } {
  const all = ranked(rows);
  const up = all.filter(({ row, rank }) => rank <= places && row.share > 0);
  const steps: Placed<R>[] = [];
  up.forEach((placed, index) => {
    if (index % 2 === 1) steps.unshift(placed);
    else steps.push(placed);
  });
  return { steps, rest: all.filter((placed) => !up.includes(placed)) };
}

/**
 * ═══ LE MINUTAGE ═══
 *
 * En secondes, depuis l'instant où les résultats s'affichent. Les marches montent
 * vides, puis les réponses se dévoilent de la dernière place à la première, et la
 * première se fait attendre un temps de plus : c'est tout le suspense. La liste du
 * dessous n'arrive qu'après, pour ne pas donner la fin avant la fin.
 *
 * ⚠️ DU CSS, PAS DES MINUTEURS. Chaque élément porte son instant (`--rv-at`) et le
 * navigateur fait le reste : une relecture des résultats pendant l'animation — un vote
 * qui arrive, un rafraîchissement — ne la relance pas et ne la coupe pas, parce qu'il
 * n'y a rien à relancer. Elle ne rejoue qu'en remontant, quand on cache puis remontre.
 */
export const TIMING = {
  /** Les marches montent, toutes ensemble. */
  rise: 0.8,
  /** La première place révélée (la plus basse). */
  first: 1.1,
  /** L'écart entre deux places. */
  between: 1.1,
  /** Le temps de plus avant le gagnant. */
  suspense: 0.7,
  /** Après le gagnant, la liste du dessous. */
  after: 1,
} as const;

/** L'instant où la réponse de ce rang se dévoile, sur un podium dont le plus bas est `lowest`. */
export function revealAt(rank: number, lowest: number): number {
  const at = TIMING.first + (lowest - rank) * TIMING.between;
  return round(rank === 1 ? at + TIMING.suspense : at);
}

/** L'instant où tout est dit : le gagnant révélé. La suite se cale dessus. */
export function winnerAt(steps: ReadonlyArray<{ rank: number }>): number {
  const lowest = steps.reduce((max, step) => Math.max(max, step.rank), 1);
  return revealAt(1, lowest);
}

/**
 * ═══ LES CONFETTIS ═══
 *
 * ⚠️ PSEUDO-ALÉATOIRES, PAS ALÉATOIRES. Tirés au hasard à chaque rendu, ils sauteraient
 * à chaque relecture des résultats ; tirés une fois dans un état, ils différeraient
 * entre l'aperçu de la régie et le mur. Une suite fixe (le nombre d'or, qui répartit
 * sans motif visible) donne la même pluie partout, et se vérifie.
 */
export type Confetto = {
  /** Point de départ, en % de la largeur. */
  x: number;
  /** Dérive horizontale pendant la chute, en % de la largeur. */
  drift: number;
  /** Tours sur lui-même pendant la chute. */
  spin: number;
  /** Durée de la chute, en secondes. */
  fall: number;
  /** Retard sur le gagnant, en secondes. */
  delay: number;
  /** Laquelle des teintes (voir `live.css`, `.podium__fx i`). */
  tint: number;
};

const GOLDEN = 0.618033988749895;
const frac = (value: number) => value - Math.floor(value);

export function confetti(count: number, tints = 4): Confetto[] {
  return Array.from({ length: count }, (_, i) => {
    const a = frac((i + 1) * GOLDEN);
    const b = frac((i + 1) * GOLDEN * 2.3);
    const c = frac((i + 1) * GOLDEN * 3.7);
    return {
      x: round(4 + a * 92),
      drift: round((b - 0.5) * 24),
      spin: round(1 + c * 3),
      fall: round(2.2 + b * 1.6),
      delay: round(c * 0.5),
      tint: i % tints,
    };
  });
}

const round = (value: number) => Math.round(value * 100) / 100;
